import { useMemo, useRef } from 'react';
import { loadRecaptcha } from '../utils/recaptchaLoader';

type RecaptchaConfig = {
  enabled?: boolean;
  version?: 'v2' | 'v3';
  publicKey?: string;
};

export function useRecaptcha(config?: RecaptchaConfig) {
  const widgetIdRef = useRef<number | null>(null);

  const ensureRendered = useMemo(() => {
    if (!config?.enabled || !config.publicKey || config.version !== 'v2') {
      return async (_?: string) => undefined as number | undefined;
    }

    return async (containerId?: string) => {
      const targetId = containerId || 'ips-waitlist-recaptcha';
      const grecaptcha = await loadRecaptcha('v2', config.publicKey!);
      if (widgetIdRef.current !== null) {
        return widgetIdRef.current;
      }
      widgetIdRef.current = grecaptcha.render(targetId, { sitekey: config.publicKey });
      return widgetIdRef.current;
    };
  }, [config?.enabled, config?.publicKey, config?.version]);

  const getToken = useMemo(() => {
    if (!config?.enabled || !config.publicKey) {
      return async (_?: string) => '';
    }

    if (config.version === 'v2') {
      return async (containerId?: string) => {
        const grecaptcha = await loadRecaptcha('v2', config.publicKey!);
        const widgetId = await ensureRendered(containerId);
        const effectiveId = widgetIdRef.current ?? widgetId;
        if (effectiveId === null || effectiveId === undefined) {
          throw new Error('Recaptcha not rendered');
        }
        return grecaptcha.getResponse(effectiveId);
      };
    }

    // default v3
    return async () => {
      const grecaptcha = await loadRecaptcha('v3', config.publicKey!);
      await new Promise<void>((resolve) => grecaptcha.ready(resolve));
      const token = await grecaptcha.execute(config.publicKey, { action: 'waitlist' });
      return token;
    };
  }, [config?.enabled, config?.publicKey, config?.version, ensureRendered]);

  return { ensureRendered, getToken };
}
